import { RequestsProvider } from 'Context';
import { UploadFilesMain } from './UploadFilesMain';
import { UpdatePersonalDetails } from './UpdatePersonalDetails';
import { useParams } from 'react-router-dom';
import { FORM_STEPS_AMOUNT, REQUESTS_FORM_ID } from 'Enum/RequestsFormId';
import {
  Box,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import { CustomStepperIcon, LinkReturnToMain, Loader } from 'Components';
import { useCallback, useEffect, useState } from 'react';
import {
  useAppSelector,
  useDictionary,
  useDispatchApp,
  useIsDesktop,
} from 'Hooks';
import { getGeneralOptions } from 'API/generalOptions/generalOptions';
import { setGeneralOptions } from 'Store';
import { UpdateBankDetails } from './UpdateBankDetails';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { ExpenseClaim } from './ExpenseClaim';
import { getPersonalDetails } from 'API/personalDetails';
import { setPersonalDetails } from 'Store/PersonalDetailsSlice';

export const Requests = () => {
  const isDesktop = useIsDesktop();
  const [isLoading, setIsLoading] = useState(true);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [steps, setSteps] = useState<string[]>([]);

  const params = useParams();

  const dispatch = useDispatchApp();
  const { generalOptions } = useAppSelector((state) => state.generalOptions);
  const initializeGeneralOptions = useCallback(async () => {
    if (!generalOptions) {
      const generalOptionsResult = await getGeneralOptions();
      if (generalOptionsResult) {
        dispatch(setGeneralOptions(generalOptionsResult));
      }
    }
  }, [dispatch, generalOptions]);

  const initializeRequests = useCallback(() => {
    const newSteps: string[] = Array(
      FORM_STEPS_AMOUNT[params.formName || 'general'] + 2
    ).fill('');
    setSteps(newSteps);
  }, [params.formName]);

  const initializePersonalDetails = useCallback(async () => {
    const result = await getPersonalDetails();
    if (result) {
      dispatch(setPersonalDetails(result));
    }
  }, [dispatch]);

  const init = useCallback(async () => {
    await initializePersonalDetails();
    await initializeGeneralOptions();
    initializeRequests();
    setIsLoading(false);
  }, [initializeGeneralOptions, initializePersonalDetails, initializeRequests]);

  useEffect(() => {
    init();
  }, [init]);

  const nextStep = () => {
    setCurrentStepIndex((prev) => {
      const index = prev + 1;
      if (index > steps.length - 1) return prev;

      window.scrollTo(0, 0);

      return index;
    });
  };

  const prevStep = () => {
    setCurrentStepIndex((prev) => {
      const index = prev - 1;
      if (index < 0) return prev;

      window.scrollTo(0, 0);
      return index;
    });
  };

  const renderForm = () => {
    if (FORM_STEPS_AMOUNT[params.formName || 'general'] <= currentStepIndex)
      return <UploadFilesMain nextStep={nextStep} prevStep={prevStep} />;

    switch (params.formName) {
      case REQUESTS_FORM_ID.UpdatePersonalDetails:
        return (
          <UpdatePersonalDetails
            nextStep={nextStep}
            currentStepIndex={currentStepIndex + 1}
            stepsLength={steps.length}
          />
        );

      case REQUESTS_FORM_ID.updateBankDetails:
        return (
          <UpdateBankDetails
            nextStep={nextStep}
            currentStepIndex={currentStepIndex + 1}
            stepsLength={steps.length}
          />
        );

      case REQUESTS_FORM_ID.expenseClaim:
        return (
          <ExpenseClaim
            nextStep={nextStep}
            currentStepIndex={currentStepIndex + 1}
            stepsLength={steps.length}
          />
        );
    }
  };

  const dictionary = useDictionary(Dictionary, DICTIONARY.Requests);

  const getTitle = () => {
    switch (params.formName) {
      case REQUESTS_FORM_ID.UpdatePersonalDetails:
        return dictionary.updatePersonalDetails;

      case REQUESTS_FORM_ID.updateBankDetails:
        return dictionary.updateBankDetails;

      case REQUESTS_FORM_ID.expenseClaim:
        return dictionary.expenseClaim;
    }
  };

  return (
    <Stack gap={isDesktop ? '46px' : '24px'}>
      <LinkReturnToMain />
      <Stack
        maxWidth={isDesktop ? '770px' : 'initial'}
        gap={isDesktop ? '56px' : '24px'}
        sx={{
          bgcolor: 'white',
          p: '32px 24px 48px',
          borderRadius: '16px',
          border: '1px solid',
          borderColor: 'primary.shade_100',
          boxShadow: '-2px -2px 12px 0px rgba(8, 30, 84, 0.04)',
          mt: '48px',
        }}
      >
        <Typography
          sx={{
            position: 'relative',
            top: '-56px',
            mt: '-48px',
            textAlign: 'center',
          }}
          variant='h2'
        >
          {getTitle()}
        </Typography>
        <span role='alert' className='sr-only'>
          {Dictionary[DICTIONARY.Requests].stepperSrLabel(
            currentStepIndex + 1,
            steps.length
          )}
        </span>
        <Stepper
          sx={{
            justifyContent: 'center',
            color: '#ec0000',
            mt: '-48px',
          }}
          activeStep={0}
          connector={
            <Box
              sx={{
                bgcolor: 'purple2.shade_200',
                borderRadius: '4px',
                height: '1px',
                width: '48px',
              }}
            ></Box>
          }
        >
          {Array(FORM_STEPS_AMOUNT[params.formName || 'general'] + 2)
            .fill('')
            .map((emptyItem, index) => {
              return (
                <Step
                  completed={index < currentStepIndex}
                  active={index === currentStepIndex}
                  key={index}
                  sx={{
                    p: 0,
                    alignItems: 'center',
                    '& .MuiStepLabel-iconContainer': {
                      p: 0,
                    },
                  }}
                >
                  <StepLabel StepIconComponent={CustomStepperIcon}></StepLabel>
                </Step>
              );
            })}
        </Stepper>
        <Box mt={'-24px'}>
          <RequestsProvider>
            {isLoading ? <Loader /> : renderForm()}
          </RequestsProvider>
        </Box>
      </Stack>
    </Stack>
  );
};
